import SalesInvoice from "@/services/api/salesInvoice";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface LoadingState {
    invoice: boolean;
    invoiceItems: boolean;
    export: boolean;
}

interface SaleOrderState {
    primaryInvoiceList: any[];
    primaryInvoiceItemsList: any[];
    loading: LoadingState;
    error: string | null;
}

const initialState: SaleOrderState = {
    primaryInvoiceList: [],
    primaryInvoiceItemsList: [],
    loading: {
        invoice: false,
        invoiceItems: false,
        export: false,
    },
    error: null,
};

export const getSalesInvoice = createAsyncThunk(
    "saleInvoice/getSalesInvoice",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await SalesInvoice.getSalesInvoice(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch sales invoice",
            );
        }
    },
);

export const getSalesInvoiceById = createAsyncThunk(
    "saleInvoice/getSalesInvoiceById",
    async (id: number, { rejectWithValue }) => {
        try {
            const response =
                await SalesInvoice.getSalesInvoiceById(id);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch sales invoice items",
            );
        }
    },
);

export const getInvoiceExport = createAsyncThunk(
    "saleInvoice/getInvoiceExport",
    async (data: any, { rejectWithValue }) => {
        try {
            const response =
                await SalesInvoice.getInvoiceExport(data);
            return saveAsExcelFile(response, "SALES_INVOICE");
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to export",
            );
        }
    },
);

const salesInvoiceSlice = createSlice({
    name: "saleInvoice",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getSalesInvoice.pending, (state) => {
                state.loading.invoice = true;
                state.error = null;
                state.primaryInvoiceList = [];
            })
            .addCase(getSalesInvoice.fulfilled, (state, action) => {
                state.loading.invoice = false;
                state.primaryInvoiceList = action.payload || [];
            })
            .addCase(getSalesInvoice.rejected, (state, action) => {
                state.loading.invoice = false;
                state.error = action.payload as string;
            })

            .addCase(getSalesInvoiceById.pending, (state) => {
                state.loading.invoiceItems = true;
                state.error = null;
                state.primaryInvoiceItemsList = [];
            })
            .addCase(getSalesInvoiceById.fulfilled, (state, action) => {
                state.loading.invoiceItems = false;
                state.primaryInvoiceItemsList = action.payload || [];
            })
            .addCase(getSalesInvoiceById.rejected, (state, action) => {
                state.loading.invoiceItems = false;
                state.error = action.payload as string;
            })

            .addCase(getInvoiceExport.pending, (state) => {
                state.loading.export = true;
                state.error = null;
            })
            .addCase(getInvoiceExport.fulfilled, (state, action) => {
                state.loading.export = false;
            })
            .addCase(getInvoiceExport.rejected, (state, action) => {
                state.loading.export = false;
                state.error = action.payload as string;
            });
    },
});

export default salesInvoiceSlice.reducer;
